import { Spin, Tag, Tooltip } from "antd";
import type {
  AiModel,
  ModelCapabilityParameters,
  ModelParameterRule,
} from "../model/ai-model";
import type { MemberPricingRule } from "../api/model-pricing-rules";
import {
  generationModeLabels,
  type GenerationMode,
} from "../model/generation-mode";

type AxisValue = string | number | boolean;

interface AxisConfig {
  field: string;
  label: string;
  values: AxisValue[];
}

interface CellOption {
  rule: MemberPricingRule;
  variantKey: string;
  variantLabel: string;
  variantIcon: string;
  tone: "low" | "medium" | "high" | "peak";
}

const defaultAxisField = "__default";
const defaultAxisValue = "default";

const parameterLabels: Record<string, string> = {
  durationSeconds: "视频时长",
  resolution: "分辨率",
  quality: "生成质量",
  size: "尺寸",
  outputFormat: "输出格式",
  aspectRatio: "宽高比",
  outputImageCount: "图片数量",
  imageCount: "图片数量",
  generateAudio: "生成音频",
  avatarTemplateId: "数字人模板",
  voiceId: "音色",
  speechRate: "语速倍率",
};

const defaultAxis: AxisConfig = {
  field: defaultAxisField,
  label: "默认价格",
  values: [defaultAxisValue],
};

function getRuleValues(value: unknown): AxisValue[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is AxisValue =>
      ["string", "number", "boolean"].includes(typeof item),
    );
  }
  return ["string", "number", "boolean"].includes(typeof value)
    ? [value as AxisValue]
    : [];
}

function getParameterValues(rule: ModelParameterRule | undefined) {
  return (
    rule?.enum.filter((value): value is AxisValue =>
      ["string", "number", "boolean"].includes(typeof value),
    ) ?? []
  );
}

function resolutionRank(value: AxisValue) {
  const normalized = String(value).trim().toLowerCase();
  const explicit: Record<string, number> = {
    "480p": 480,
    "540p": 540,
    "720p": 720,
    "1080p": 1080,
    "2k": 1440,
    "4k": 2160,
    "8k": 4320,
  };
  if (explicit[normalized] !== undefined) return explicit[normalized];
  const pMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*p$/);
  if (pMatch?.[1]) return Number(pMatch[1]);
  const kMatch = normalized.match(/^(\d+(?:\.\d+)?)\s*k$/);
  if (kMatch?.[1]) return Number(kMatch[1]) * 720;
  const sizeMatch = normalized.match(/^(\d+)\s*[x×]\s*(\d+)$/);
  if (sizeMatch?.[2]) return Number(sizeMatch[2]);
  return undefined;
}

function compareAxis(field: string, left: AxisValue, right: AxisValue) {
  if (field === defaultAxisField) return 0;
  if (field === "resolution") {
    const leftRank = resolutionRank(left);
    const rightRank = resolutionRank(right);
    if (leftRank !== undefined && rightRank !== undefined)
      return leftRank - rightRank;
    if (leftRank !== undefined) return -1;
    if (rightRank !== undefined) return 1;
  }
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber))
    return leftNumber - rightNumber;
  return String(left).localeCompare(String(right), "zh-Hans-CN", {
    numeric: true,
  });
}

function formatValue(field: string, value: AxisValue) {
  if (field === defaultAxisField) return "默认";
  if (field === "durationSeconds" && typeof value === "number")
    return `${value} 秒`;
  if (typeof value === "boolean") return value ? "是" : "否";
  return String(value);
}

function getFieldLabel(field: string) {
  return parameterLabels[field] ?? field;
}

function getVariantIcon(field: string | undefined) {
  if (!field) return "●";
  if (
    field === "generateAudio" ||
    field === "voiceId" ||
    field === "speechRate"
  )
    return "♪";
  if (field === "outputImageCount" || field === "imageCount") return "#";
  if (field === "aspectRatio" || field === "size") return "□";
  if (field === "avatarTemplateId") return "◇";
  return "•";
}

function uniqueValues(values: AxisValue[], field: string) {
  const map = new Map(values.map((value) => [String(value), value]));
  return [...map.values()].sort((left, right) =>
    compareAxis(field, left, right),
  );
}

function createRuleAxis(
  field: string,
  rules: MemberPricingRule[],
  parameters: ModelCapabilityParameters | undefined,
) {
  const ruleValues = rules.flatMap((rule) =>
    getRuleValues(rule.condition[field]),
  );
  const values =
    ruleValues.length > 0
      ? uniqueValues(ruleValues, field)
      : uniqueValues(getParameterValues(parameters?.properties[field]), field);
  if (values.length === 0) return undefined;
  return { field, label: getFieldLabel(field), values };
}

function pickAxes(
  model: AiModel,
  rules: MemberPricingRule[],
  parameters: ModelCapabilityParameters | undefined,
): { row: AxisConfig; column: AxisConfig } | undefined {
  if (model.mediaKind === "video") {
    const duration = createRuleAxis("durationSeconds", rules, parameters);
    const resolution = createRuleAxis("resolution", rules, parameters);
    if (duration && resolution) return { row: duration, column: resolution };
  }
  if (model.mediaKind === "image") {
    const quality = createRuleAxis("quality", rules, parameters);
    const resolution =
      createRuleAxis("resolution", rules, parameters) ??
      createRuleAxis("size", rules, parameters);
    if (quality && resolution) return { row: quality, column: resolution };
    if (resolution) return { row: defaultAxis, column: resolution };
  }
  const row =
    createRuleAxis("durationSeconds", rules, parameters) ??
    createRuleAxis("quality", rules, parameters) ??
    createRuleAxis("outputImageCount", rules, parameters);
  const column =
    createRuleAxis("resolution", rules, parameters) ??
    createRuleAxis("size", rules, parameters) ??
    createRuleAxis("outputFormat", rules, parameters);
  if (row && column) return { row, column };
  if (column) return { row: defaultAxis, column };
  return undefined;
}

function getPointTone(points: number, min: number, max: number) {
  const ratio = max === min ? 0 : (points - min) / (max - min);
  if (ratio <= 0.33) return "low";
  if (ratio <= 0.66) return "medium";
  if (ratio <= 0.85) return "high";
  return "peak";
}

function getVariant(rule: MemberPricingRule, axisFields: Set<string>) {
  const entries = Object.entries(rule.condition)
    .filter(([field]) => !axisFields.has(field))
    .flatMap(([field, value]) => {
      const values = getRuleValues(value);
      return values.length > 0
        ? [
            {
              field,
              label: getFieldLabel(field),
              value: values.map((item) => formatValue(field, item)).join(" / "),
            },
          ]
        : [];
    });
  if (entries.length === 0) {
    return { key: "default", label: "默认", icon: getVariantIcon(undefined) };
  }
  return {
    key: entries.map((entry) => `${entry.field}:${entry.value}`).join("|"),
    label: entries.map((entry) => `${entry.label} ${entry.value}`).join(" / "),
    icon: getVariantIcon(entries[0]?.field),
  };
}

function cellKey(rowValue: AxisValue, columnValue: AxisValue) {
  return `${String(rowValue)}::${String(columnValue)}`;
}

function buildCells(
  rules: MemberPricingRule[],
  axes: { row: AxisConfig; column: AxisConfig },
) {
  const min = Math.min(...rules.map((rule) => rule.points));
  const max = Math.max(...rules.map((rule) => rule.points));
  const axisFields = new Set([
    axes.row.field,
    axes.column.field,
    defaultAxisField,
  ]);
  const cells = new Map<string, CellOption[]>();

  rules.forEach((rule) => {
    const rowValues =
      axes.row.field === defaultAxisField
        ? axes.row.values
        : getRuleValues(rule.condition[axes.row.field]);
    const columnValues = getRuleValues(rule.condition[axes.column.field]);
    const visibleRows = rowValues.length > 0 ? rowValues : axes.row.values;
    const visibleColumns =
      columnValues.length > 0 ? columnValues : axes.column.values;
    const variant = getVariant(rule, axisFields);
    visibleRows.forEach((rowValue) => {
      visibleColumns.forEach((columnValue) => {
        const key = cellKey(rowValue, columnValue);
        const options = cells.get(key) ?? [];
        if (!options.some((option) => option.variantKey === variant.key)) {
          options.push({
            rule,
            variantKey: variant.key,
            variantLabel: variant.label,
            variantIcon: variant.icon,
            tone: getPointTone(rule.points, min, max),
          });
        }
        cells.set(key, options);
      });
    });
  });
  return cells;
}

export function ModelPricingMatrix({
  model,
  mode,
  rules,
  isLoading = false,
  isError = false,
}: {
  model: AiModel;
  mode: GenerationMode;
  rules: MemberPricingRule[];
  isLoading?: boolean;
  isError?: boolean;
}) {
  const parameters = model.capabilityParameters?.[mode];
  const axes = pickAxes(model, rules, parameters);
  const cells =
    axes && rules.length > 0
      ? buildCells(rules, axes)
      : new Map<string, CellOption[]>();

  if (isLoading) {
    return (
      <div className="model-detail-empty-panel">
        <Spin />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="model-detail-empty-panel">
        定价规则暂时加载失败，请稍后重试。
      </div>
    );
  }

  if (rules.length === 0) {
    return (
      <div className="model-detail-empty-panel">
        当前能力暂未配置会员可见定价规则，提交前将按服务端实时报价。
      </div>
    );
  }

  if (!axes) {
    return (
      <div className="model-detail-empty-panel">
        当前定价规则不足以生成二维矩阵；实际消耗以提交前报价为准。
      </div>
    );
  }

  return (
    <div className="web-pricing-matrix">
      <div className="web-pricing-head">
        <div>
          <h3>定价矩阵</h3>
          <p>
            {axes.row.label} ↓ / {axes.column.label} →
          </p>
        </div>
        <Tag bordered={false}>{generationModeLabels[mode]}</Tag>
      </div>
      <div className="web-pricing-scroll">
        <table>
          <thead>
            <tr>
              <th>
                {axes.row.label} ↓ / {axes.column.label} →
              </th>
              {axes.column.values.map((value) => (
                <th key={String(value)}>
                  {formatValue(axes.column.field, value)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {axes.row.values.map((rowValue) => (
              <tr key={String(rowValue)}>
                <th>{formatValue(axes.row.field, rowValue)}</th>
                {axes.column.values.map((columnValue) => {
                  const options =
                    cells.get(cellKey(rowValue, columnValue)) ?? [];
                  return (
                    <td key={String(columnValue)}>
                      {options.length > 0 ? (
                        <div className="web-price-options">
                          {options.map((option) => (
                            <Tooltip
                              key={option.variantKey}
                              title={option.variantLabel}
                            >
                              <div
                                className={`web-price-cell tone-${option.tone}`}
                              >
                                <i aria-hidden="true">{option.variantIcon}</i>
                                <strong>{option.rule.points}</strong>
                                <span>积分</span>
                              </div>
                            </Tooltip>
                          ))}
                        </div>
                      ) : (
                        <span className="web-price-empty">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="web-pricing-note">
        价目表来自会员可见定价规则；最终消耗仍以提交前报价确认为准。
      </p>
    </div>
  );
}
